import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, DialogHandler, IconButton } from "@etain/ui";
import { Bell } from "lucide-react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api.js";
import { formatDateTime } from "../lib/format.js";
import {
  notificationPath,
  type InboxNotification,
  type NotificationInboxResponse,
} from "../lib/notification-inbox.js";
import { useSession } from "../session.js";
import { EmptyState, ErrorState, LoadingState } from "./Page.js";

export function NotificationInbox({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: session } = useSession();
  const queryClient = useQueryClient();
  const allowed = Boolean(session && (session.features.crm || session.features.projects));
  const key = [
    "notification-inbox",
    session?.activeTenant.id,
    session?.user.id,
    session?.features.crm,
    session?.features.projects,
  ];
  const inbox = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => api.get<NotificationInboxResponse>("/notifications", signal),
    enabled: allowed,
    refetchInterval: 30_000,
    retry: (count, error) =>
      !(error instanceof ApiError && [401, 403].includes(error.status)) && count < 1,
  });
  const markRead = useMutation({
    mutationFn: (item: InboxNotification) =>
      api.patch(`/notifications/${item.source}/${encodeURIComponent(item.id)}/read`, {}),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["notification-inbox"] }),
        queryClient.invalidateQueries({ queryKey: ["crm", "reminders"] }),
        queryClient.invalidateQueries({ queryKey: ["project-notifications"] }),
      ]);
    },
  });
  // Do not expose cached content after an access or transport failure.
  const data = allowed && !inbox.isError ? inbox.data : undefined;
  const unreadCount = data?.unreadCount ?? 0;
  const label = inbox.isError
    ? "Benachrichtigungen konnten nicht geladen werden"
    : inbox.isLoading && allowed
      ? "Benachrichtigungen werden geladen"
      : `Benachrichtigungen: ${unreadCount} ungelesen`;
  return (
    <>
      <IconButton
        className="topbar__optional notification-bell"
        aria-label={label}
        title={label}
        onClick={() => onOpenChange(true)}
      >
        <Bell aria-hidden="true" />
        {unreadCount > 0 ? (
          <span className="notification-bell__count" aria-hidden="true">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        ) : null}
        {inbox.isError ? <span aria-hidden="true">!</span> : null}
      </IconButton>
      <DialogHandler
        open={open}
        onOpenChange={onOpenChange}
        title="Benachrichtigungen"
        description={`Ihre CRM- und Projekthinweise für ${session?.activeTenant.name ?? "den aktiven Mandanten"}.`}
        cancelAction={
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        }
      >
        {allowed ? (
          <Button
            variant="secondary"
            size="small"
            busy={inbox.isFetching}
            onClick={() => void inbox.refetch()}
          >
            Aktualisieren
          </Button>
        ) : null}
        {allowed && inbox.isLoading ? (
          <LoadingState cards={1} />
        ) : allowed && inbox.isError ? (
          <ErrorState error={inbox.error} retry={() => void inbox.refetch()} />
        ) : data?.items.length ? (
          <>
            <p role="status">{unreadCount} ungelesen · Ungelesene Hinweise zuerst</p>
            <ul className="notification-inbox">
              {data.items.map((item) => {
                const path = notificationPath(item);
                return (
                  <li key={`${item.source}:${item.id}`}>
                    <p className="notification-inbox__context">
                      {item.source === "crm" ? "CRM" : "Projekt"} · {item.context}
                    </p>
                    {path ? (
                      <Link to={path} onClick={() => onOpenChange(false)}>
                        {item.title}
                      </Link>
                    ) : (
                      <strong>{item.title}</strong>
                    )}
                    {item.body ? <p>{item.body}</p> : null}
                    <p>
                      {formatDateTime(item.createdAt)} · {item.readAt ? "Gelesen" : "Ungelesen"}
                    </p>
                    {!item.readAt ? (
                      <Button
                        size="small"
                        variant="quiet"
                        busy={markRead.isPending && markRead.variables === item}
                        disabled={markRead.isPending}
                        onClick={() => markRead.mutate(item)}
                      >
                        Als gelesen markieren
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
            {data.items.length >= data.limit ? (
              <p>
                Die neuesten {data.limit} Hinweise werden angezeigt. Der Zähler umfasst alle
                ungelesenen Hinweise.
              </p>
            ) : null}
          </>
        ) : (
          <EmptyState
            title="Keine Benachrichtigungen"
            description="Fällige CRM-Erinnerungen und Hinweise zu beobachteten Projekten erscheinen hier."
          />
        )}
        {markRead.isError ? <p role="alert">{markRead.error.message}</p> : null}
      </DialogHandler>
    </>
  );
}
